/**
 * API endpoint paths (WEBD-19).
 *
 * The one registry of backend route paths. Only the typed endpoint modules
 * in shared/api/*Api.ts read it; hooks and features call those modules.
 * Query strings are passed as axios `params` by the endpoint functions, not
 * baked into these paths.
 */

const seg = (value: string) => encodeURIComponent(value);

export const API_ENDPOINTS = {
  AUTH: {
    ME: '/auth/me',
  },

  PORTFOLIOS: {
    LIST: '/portfolios',
    HOLDINGS_SUMMARY: (id: string) => `/portfolios/${seg(id)}/holdings-summary`,
    ACCOUNTS_VIEW: (id: string) => `/portfolios/${seg(id)}/accounts-view`,
    EXPORT: (id: string) => `/portfolios/${seg(id)}/export`,
    DAILY_CHANGE: (id: string) => `/portfolios/${seg(id)}/daily-change`,
    ALLOCATION: (id: string) => `/portfolios/${seg(id)}/allocation`,
    REBALANCER_HOLDINGS: (id: string) => `/portfolios/${seg(id)}/rebalancer/holdings`,
    REBALANCER_CALCULATE: (id: string) => `/portfolios/${seg(id)}/rebalancer/calculate`,
    REBALANCER_INCOME_IMPACT: (id: string) => `/portfolios/${seg(id)}/rebalancer/income-impact`,
    REBALANCER_APPLY_PROFILE: (id: string) => `/portfolios/${seg(id)}/rebalancer/apply-profile`,
    /** Income Estimator projection, holdings and dividends derived server-side (WEBD-23). */
    INCOME_PROJECTIONS: (id: string) => `/portfolios/${seg(id)}/income-projections`,
    /** Cost Basis page reconciliation for every account, variance included (WEBD-23). */
    COST_BASIS: (id: string) => `/portfolios/${seg(id)}/cost-basis`,
  },

  ACCOUNTS: {
    BY_PORTFOLIO: (portfolioId: string) => `/portfolios/${seg(portfolioId)}/accounts`,
    GET: (id: string) => `/accounts/${seg(id)}`,
    DISCONNECT: (id: string) => `/accounts/${seg(id)}/disconnect`,
    SETTLEMENT_FUND: (id: string) => `/accounts/${seg(id)}/settlement-fund`,
    SETTLEMENT_FUND_DETECT: (id: string) => `/accounts/${seg(id)}/settlement-fund/detect`,
    SETTLEMENT_FUND_DISMISS: (id: string) => `/accounts/${seg(id)}/settlement-fund/dismiss`,
    SETTLEMENT_FUND_SUGGESTIONS: (portfolioId: string) => `/portfolios/${seg(portfolioId)}/settlement-fund/suggestions`,
  },

  HOLDINGS: {
    BY_ACCOUNT: (accountId: string) => `/accounts/${seg(accountId)}/holdings`,
    BY_PORTFOLIO: (portfolioId: string) => `/portfolios/${seg(portfolioId)}/holdings`,
    GET: (id: string) => `/holdings/${seg(id)}`,
    CONVERT_TO_MANUAL: (id: string) => `/holdings/${seg(id)}/convert-to-manual`,
    COST_BASIS: (id: string) => `/holdings/${seg(id)}/cost-basis`,
    MANUAL_LOTS: (id: string) => `/holdings/${seg(id)}/manual-lots`,
    MANUAL_LOT: (id: string, lotId: string) => `/holdings/${seg(id)}/manual-lots/${seg(lotId)}`,
    CLEANUP_EXPIRED_OPTIONS: '/holdings/cleanup/expired-options',
  },

  TRANSACTIONS: {
    BY_ACCOUNT: (accountId: string) => `/accounts/${seg(accountId)}/transactions`,
    BY_PORTFOLIO: (portfolioId: string) => `/portfolios/${seg(portfolioId)}/transactions`,
    GET: (id: string) => `/transactions/${seg(id)}`,
    CORPORATE_ACTIONS_NEEDING_REVIEW: '/transactions/corporate-actions/needs-review',
    RESOLVE_CORPORATE_ACTION: (id: string) => `/transactions/${seg(id)}/resolve-corporate-action`,
  },

  INCOME: {
    BY_PORTFOLIO: (portfolioId: string) => `/portfolios/${seg(portfolioId)}/income`,
    BREAKDOWNS: (portfolioId: string) => `/portfolios/${seg(portfolioId)}/income/breakdowns`,
    RESOLVE_OPTION: (id: string) => `/income/${seg(id)}/resolve-option`,
    SPLIT_RESOLVE_OPTION: (id: string) => `/income/${seg(id)}/split-resolve-option`,
    RESOLVE_CONFLICTS_BATCH: '/income/resolve-conflicts-batch',
  },

  TAX_PAYMENTS: {
    LIST: '/taxes/payments',
    BULK: '/taxes/payments/bulk',
    GET: (id: string) => `/taxes/payments/${seg(id)}`,
  },

  EXTERNAL_INCOME: {
    LIST: '/external-income',
    BULK: '/external-income/bulk',
    BY_TYPE: '/external-income/by-type',
    /** Preview of the server's estimate for a source (no persistence, WEBD-02). */
    ESTIMATE: '/external-income/estimate',
    GET: (id: string) => `/external-income/${seg(id)}`,
  },

  TAX: {
    CALCULATE_FULL: '/tax/calculate-full',
    HARVEST_PLAN: '/tax/harvest-plan',
    VALIDATE_401K: '/tax/validate-401k',
  },

  CALCULATIONS: {
    REALIZED_GAINS: '/calculations/realized-gains',
    PORTFOLIO_REALIZED_GAINS: (portfolioId: string) => `/calculations/realized-gains/${seg(portfolioId)}`,
    REALIZED_GAINS_YEARS: (accountId: string) => `/calculations/realized-gains-years/${seg(accountId)}`,
    COST_BASIS: '/calculations/cost-basis',
  },

  LOTS: {
    ASSIGNMENTS: (saleTransactionId: string) => `/lots/assignments/${seg(saleTransactionId)}`,
    RECALCULATE_ACCOUNT: '/lots/recalculate-account',
  },

  PERFORMANCE: {
    ALL: '/performance/all',
    BENCHMARK: '/performance/benchmark',
  },

  OPTIONS: {
    CONFIG: '/options/config',
    CC_OPTIMIZER: '/options/cc-optimizer',
    CC_COVERAGE: '/options/cc-coverage',
    CSP_SCENARIOS: '/options/csp-scenarios',
  },

  MARKET: {
    QUOTE: (symbol: string) => `/market/quote/${seg(symbol)}`,
    QUOTES: '/market/quotes',
    OPTIONS_CHAIN: (symbol: string) => `/market/options-chain/${seg(symbol)}`,
    OPTIONS_CHAINS_BATCH: '/market/options-chains/batch',
    UPCOMING_DIVIDENDS: '/market/upcoming-dividends',
    SECURITY_METADATA: '/market/security-metadata',
    MOVERS: '/market/movers',
    NEWS: '/market/news',
    SYMBOL_NEWS: (symbol: string) => `/market/news/${seg(symbol)}`,
    CHART: (symbol: string) => `/market/chart/${seg(symbol)}`,
    PROVIDER_STATS: '/market/provider-stats',
    QUOTE_CACHE_STATS: '/market/quote-cache/stats',
    CACHE_CLEAR_QUOTES: '/market/cache/clear-quotes',
    CACHE_CLEAR_DIVIDENDS: '/market/cache/clear-dividends',
    CACHE_CLEAR_OPTIONS: '/market/cache/clear-options',
    CACHE_CLEAR_GREEKS: '/market/cache/clear-greeks',
    CACHE_CLEAR_ALL: '/market/cache/clear-all',
  },

  STRATEGIES: {
    LIST: '/strategies',
    GET: (id: string) => `/strategies/${seg(id)}`,
    REFRESH: (id: string) => `/strategies/${seg(id)}/refresh`,
  },

  WATCHLISTS: {
    LIST: '/watchlists',
    GET: (id: string) => `/watchlists/${seg(id)}`,
    SYMBOLS: (id: string) => `/watchlists/${seg(id)}/symbols`,
    SYMBOL: (id: string, symbol: string) => `/watchlists/${seg(id)}/symbols/${seg(symbol)}`,
    REORDER_SYMBOLS: (id: string) => `/watchlists/${seg(id)}/symbols/reorder`,
  },

  TARGET_ALLOCATION_PROFILES: {
    LIST: '/target-allocation-profiles',
    GET: (id: string) => `/target-allocation-profiles/${seg(id)}`,
  },

  IMPORT: {
    PREVIEW_TEXT: '/import/preview-text',
    RECONCILE: '/import/reconcile',
    PLANS: '/import/plans',
    PLAN: (id: string) => `/import/plans/${seg(id)}`,
    PLAN_COMMIT: (id: string) => `/import/plans/${seg(id)}/commit`,
  },

  MESSAGES: {
    LIST: '/messages',
    UNREAD_COUNT: '/messages/unread-count',
    GET: (id: string) => `/messages/${seg(id)}`,
    MARK_READ: (id: string) => `/messages/${seg(id)}/read`,
    MARK_ACTIONED: (id: string) => `/messages/${seg(id)}/action`,
  },

  SNAPTRADE: {
    STATUS: '/snaptrade/status',
    CONNECT: '/snaptrade/connect',
    PENDING_ACCOUNTS: '/snaptrade/pending-accounts',
    LINK_ACCOUNTS: '/snaptrade/link-accounts',
    CONNECTIONS: '/snaptrade/connections',
    CONNECTION: (connectionId: string) => `/snaptrade/connections/${seg(connectionId)}`,
    SYNC: (accountId: string) => `/snaptrade/sync/${seg(accountId)}`,
    DAILY_SYNC: '/snaptrade/daily-sync',
    REFRESH: (connectionId: string) => `/snaptrade/refresh/${seg(connectionId)}`,
    REFRESH_TRANSACTIONS: (connectionId: string) => `/snaptrade/refresh-transactions/${seg(connectionId)}`,
    RECONNECTED: (connectionId: string) => `/snaptrade/reconnected/${seg(connectionId)}`,
    RESOLVE_CONFLICTS_BATCH: '/snaptrade/transactions/resolve-conflicts-batch',
    UPDATE_OPTION_CLASSIFICATION: '/snaptrade/transactions/update-classification',
    BROKERAGES: '/snaptrade/brokerages',
    DEBUG: '/snaptrade/debug',
    AUTHORIZATION: (authorizationId: string) => `/snaptrade/authorizations/${seg(authorizationId)}`,
    ADMIN_USERS: '/snaptrade/admin/users',
    ADMIN_USER: (snaptradeUserId: string) => `/snaptrade/admin/users/${seg(snaptradeUserId)}`,
    RESET_CREDENTIALS: '/snaptrade/reset-credentials',
    LOCAL_CONNECTION: (id: string) => `/snaptrade/local-connections/${seg(id)}`,
  },

  CHAT: {
    SEND: '/chat',
  },

  API_KEYS: {
    LIST: '/api-keys',
    GET: (id: string) => `/api-keys/${seg(id)}`,
  },
} as const;
